import { getDepositLimitConfig, getRollingLimits, putRollingLimits } from 'src_server/trpc/types/responsibleGambling';

export type DepositLimitWindowType = 'calendar' | 'rolling';
export type DepositLimitConfigKeyType =
    `${number}:${getRollingLimits.CalculationTypeSchemaType}:${getDepositLimitConfig.DepositLimitConfigLimitTypeSchemaType}`;

export const depositLimitConfigKey = (
    windowLengthInDays: number,
    calculationType: getRollingLimits.CalculationTypeSchemaType,
    limitType: getDepositLimitConfig.DepositLimitConfigLimitTypeSchemaType
): DepositLimitConfigKeyType => `${windowLengthInDays}:${calculationType}:${limitType}`;

export type DepositLimitConfigMapType = Map<
    DepositLimitConfigKeyType,
    getDepositLimitConfig.DepositLimitConfigObjectSchemaType
>;

export const buildDepositLimitConfigMap = (
    limitsConfig: ReadonlyArray<getDepositLimitConfig.DepositLimitConfigObjectSchemaType>
): DepositLimitConfigMapType => {
    const map: DepositLimitConfigMapType = new Map();
    for (const config of limitsConfig) {
        map.set(depositLimitConfigKey(config.windowLengthInDays, config.calculationType, config.limitType), config);
    }
    return map;
};

const fallbackLimitType = (
    calculationType: getRollingLimits.CalculationTypeSchemaType
): putRollingLimits.LimitObjectType['limitType'] => {
    return calculationType === 'gross' ? 'calendar' : 'rolling';
};

const configLimitType = (
    windowLengthInDays: number,
    calculationType: getRollingLimits.CalculationTypeSchemaType,
    depositLimitConfigMap?: DepositLimitConfigMapType
): putRollingLimits.LimitObjectType['limitType'] | undefined => {
    if (depositLimitConfigMap === undefined) {
        return undefined;
    }

    let matched: putRollingLimits.LimitObjectType['limitType'] | undefined;
    for (const config of depositLimitConfigMap.values()) {
        if (config.windowLengthInDays !== windowLengthInDays || config.calculationType !== calculationType) {
            continue;
        }
        if (config.limitType === 'calendar' || config.limitType === 'rolling') {
            matched = config.limitType;
        }
    }

    return matched;
};

export const resolvePutRollingLimitType = (
    windowLengthInDays: number,
    calculationType: getRollingLimits.CalculationTypeSchemaType,
    depositLimitConfigMap?: DepositLimitConfigMapType
): putRollingLimits.LimitObjectType['limitType'] => {
    return (
        configLimitType(windowLengthInDays, calculationType, depositLimitConfigMap) ??
        fallbackLimitType(calculationType)
    );
};

export const resolveAccountEditCalculationType = (
    storedCalculationType: getRollingLimits.CalculationTypeSchemaType | undefined,
    selectedCalculationType: getRollingLimits.CalculationTypeSchemaType
): getRollingLimits.CalculationTypeSchemaType => {
    return storedCalculationType ?? selectedCalculationType;
};

export const resolveAccountEditLimitType = (
    storedLimitType: string | undefined,
    windowLengthInDays: number,
    calculationType: getRollingLimits.CalculationTypeSchemaType,
    depositLimitConfigMap?: DepositLimitConfigMapType
): putRollingLimits.LimitObjectType['limitType'] => {
    if (storedLimitType === 'calendar' || storedLimitType === 'rolling') {
        return storedLimitType;
    }

    return resolvePutRollingLimitType(windowLengthInDays, calculationType, depositLimitConfigMap);
};

/** A stored Deposit Limit (gross) must not be switched to a Net Deposit Limit. */
export const isGrossToNetLimitChange = (
    previousCalculationType: getRollingLimits.CalculationTypeSchemaType | undefined,
    nextCalculationType: getRollingLimits.CalculationTypeSchemaType
): boolean => previousCalculationType === 'gross' && nextCalculationType === 'net';

/** Calendar windows use Daily / Weekly / Monthly copy; rolling windows use 1 / 7 / 30 days. */
export const shouldUseCalendarPeriodLabels = (limitType: DepositLimitWindowType): boolean => {
    return limitType === 'calendar';
};

/**
 * Calendar vs rolling:
 * 1. the user's rolling limit, when that window is already set
 * 2. the operator config for the same window and calculation type, including inactive rows
 * 3. gross → calendar, net → rolling
 */
export const resolveAccountLimitWindowType = (
    storedLimitType: string | undefined,
    windowLengthInDays: number,
    calculationType: getRollingLimits.CalculationTypeSchemaType,
    depositLimitConfigMap?: DepositLimitConfigMapType
): DepositLimitWindowType => {
    if (storedLimitType === 'calendar' || storedLimitType === 'rolling') {
        return storedLimitType;
    }

    return resolvePutRollingLimitType(windowLengthInDays, calculationType, depositLimitConfigMap);
};

const enabledConfigLimitType = (
    windowLengthInDays: number,
    depositLimitConfigMap?: DepositLimitConfigMapType
): DepositLimitWindowType | undefined => {
    if (depositLimitConfigMap === undefined) {
        return undefined;
    }

    let hasCalendar = false;
    let hasRolling = false;
    for (const config of depositLimitConfigMap.values()) {
        if (config.windowLengthInDays !== windowLengthInDays || config.limitEnabled !== true) {
            continue;
        }
        if (config.limitType === 'calendar') {
            hasCalendar = true;
        }
        if (config.limitType === 'rolling') {
            hasRolling = true;
        }
    }

    if (hasRolling) {
        return 'rolling';
    }
    if (hasCalendar) {
        return 'calendar';
    }

    return undefined;
};

/**
 * Signup: the user picks gross or net. Gross is always a calendar window.
 * Net uses the operator config for that window, including inactive rows, then rolling.
 * Before a choice, the label follows an enabled config row.
 * With no enabled row and no choice, the label is rolling (X Day). Nothing is saved.
 */
export const resolveSignupLimitWindowType = (
    windowLengthInDays: number,
    calculationType: getRollingLimits.CalculationTypeSchemaType | undefined,
    depositLimitConfigMap?: DepositLimitConfigMapType
): DepositLimitWindowType => {
    if (calculationType === 'gross') {
        return 'calendar';
    }

    if (calculationType === undefined) {
        return enabledConfigLimitType(windowLengthInDays, depositLimitConfigMap) ?? 'rolling';
    }

    return resolvePutRollingLimitType(windowLengthInDays, 'net', depositLimitConfigMap);
};
